import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { MapPin, Users, Shield, CreditCard, Smartphone, Clock, CheckCircle, ArrowRight, Zap } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { 
  Accordion, 
  AccordionContent, 
  AccordionItem, 
  AccordionTrigger 
} from '../components/ui/accordion';

const Home = () => {
  const [heroRef, heroInView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [featuresRef, featuresInView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [howItWorksRef, howItWorksInView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [examplesRef, examplesInView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [futureRef, futureInView] = useInView({ triggerOnce: true, threshold: 0.1 });

  const features = [
    {
      icon: <MapPin className="w-8 h-8" />,
      title: "Interaktīva Karte",
      description: "Ērti izvietotie marķieri kartē parāda jūsu apkārtnes pieejamos pieprasījumus un piedāvājumus reāllaikā."
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: "Marķieru Filtrēšana",
      description: "Filtrējiet marķierus pēc kategorijām un iestatiet radiusu, lai redzētu tikai jums nepieciešamajā attālumā esošos pieprasījumus."
    },
    {
      icon: <Clock className="w-8 h-8" />,
      title: "Ātra Atbilde",
      description: "Saņemiet vai sniedziet palīdzību dažu minūšu laikā. Nekavējoties sazinieties ar cilvēkiem."
    },
    {
      icon: <Smartphone className="w-8 h-8" />,
      title: "Vienkārša Lietošana",
      description: "Intuitīvs dizains, kas ļauj izveidot vai atrast palīdzības pieprasījumus ar dažiem klikšķiem."
    }
  ];

  const examples = [
    {
      image: "https://customer-assets.emergentagent.com/job_community-markers/artifacts/duanu517_Telemap1.png",
      title: "Rādiusa Iestatīšana",
      description: "Iestatiet sev ērtāko rādiusu, lai redzētu tikai nepieciešamajā attālumā esošos pieprasījumus."
    },
    {
      image: "https://customer-assets.emergentagent.com/job_community-markers/artifacts/oiglligv_Telemap2.png",
      title: "Kategoriju Izvēle",
      description: "Izvēlieties starp dažādām kategorijām - transports, sadzīve, rīki, pirkšana un citas."
    },
    {
      image: "https://customer-assets.emergentagent.com/job_community-markers/artifacts/3dz2xslp_Telemap3.png",
      title: "Filtrēšana",
      description: "Filtrējiet marķierus pēc tipa - piedāvājums vai vajadzība, un pēc izvēlētajām kategorijām."
    }
  ];

  const futurePlans = [
    {
      icon: <Shield className="w-12 h-12" />,
      title: "Lietotāju Verifikācija",
      description: "Papildus drošībai būs iespēja verificēt savu profilu, lai palielinātu uzticamību starp lietotājiem."
    },
    {
      icon: <CreditCard className="w-12 h-12" />,
      title: "Maksājumu Sistēma",
      description: "Integrēta un droša maksājumu sistēma, kas ļaus ērti norēķināties par sniegtajiem pakalpojumiem tieši aplikācijā."
    },
    {
      icon: <Zap className="w-12 h-12" />,
      title: "Premium Funkcijas",
      description: "Pievienojiet vairāk marķierus, prioritārie pieprasījumi un citas ekskluzīvas iespējas aktīviem lietotājiem."
    }
  ];

  const steps = [
    {
      number: "01",
      title: "Lejupielādē Aplikāciju",
      description: "Instalējiet Telemap no App Store vai Google Play veikala savā mobilajā ierīcē."
    },
    {
      number: "02",
      title: "Izveidojiet Profilu",
      description: "Reģistrējieties ar e-pastu vai telefona numuru un izveidojiet savu profilu."
    },
    {
      number: "03",
      title: "Marķējiet Kartē",
      description: "Izvietojiet marķieri kartē ar savu pieprasījumu vai piedāvājumu palīdzēt citiem."
    },
    {
      number: "04",
      title: "Savienojieties un Palīdziet",
      description: "Sazinieties ar cilvēkiem, kas var palīdzēt vai kam nepieciešama jūsu palīdzība."
    }
  ];

  const handleAppStoreClick = () => {
    window.open('https://apps.apple.com/lv/app/telemap/id6748620193', '_blank');
  };

  const handleGooglePlayClick = () => {
    window.open('https://play.google.com/store/apps/details?id=com.telemap.lv', '_blank');
  };

  return (
    <div className="min-h-screen">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-bg-primary/95 backdrop-blur-md border-b border-border-subtle">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_community-markers/artifacts/kkamc9mv_logo.png" 
                alt="Telemap Logo" 
                className="w-12 h-12"
              />
              <span className="text-2xl font-bold text-text-primary">Telemap</span>
            </div>
            <nav className="hidden md:flex items-center justify-center flex-1">
              <div className="flex items-center space-x-8">
                <a href="#features" className="text-text-secondary hover:text-accent-primary transition-colors">Funkcijas</a>
                <a href="#how-it-works" className="text-text-secondary hover:text-accent-primary transition-colors">Kā Tas Darbojas</a>
                <a href="#examples" className="text-text-secondary hover:text-accent-primary transition-colors">Piemēri</a>
                <a href="#future" className="text-text-secondary hover:text-accent-primary transition-colors">Nākotne</a>
              </div>
            </nav>
            <div className="w-8"></div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section ref={heroRef} className="pt-32 pb-20 px-6 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-bg-primary via-bg-secondary to-bg-primary opacity-50"></div>
        <div className="container mx-auto relative z-10">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            animate={heroInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="grid md:grid-cols-2 gap-12 items-center"
          >
            <div>
              <div className="inline-block px-4 py-2 bg-accent-bg border border-accent-primary/30 rounded-full mb-6">
                <span className="text-accent-primary text-sm font-semibold">🚀 Tagad Pieejams Latvijā</span>
              </div>
              <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
                Atbalsts <span className="text-accent-primary">Viena Klikšķa Attālumā</span>
              </h1>
              <p className="text-xl text-text-secondary mb-8 leading-relaxed">
                Telemap ir mobilā aplikācija, kas savieno cilvēkus, kuri meklē palīdzību ar tiem, kas ir gatavi to sniegt. Vienkārši, ātri un efektīvi.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <Button 
                  onClick={handleAppStoreClick}
                  className="btn-primary flex items-center justify-center space-x-2"
                >
                  <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18.71 19.5C17.88 20.74 17 21.95 15.66 21.97C14.32 22 13.89 21.18 12.37 21.18C10.84 21.18 10.37 21.95 9.09997 22C7.78997 22.05 6.79997 20.68 5.95997 19.47C4.24997 17 2.93997 12.45 4.69997 9.39C5.56997 7.87 7.12997 6.91 8.81997 6.88C10.1 6.86 11.32 7.75 12.11 7.75C12.89 7.75 14.37 6.68 15.92 6.84C16.57 6.87 18.39 7.1 19.56 8.82C19.47 8.88 17.39 10.1 17.41 12.63C17.44 15.65 20.06 16.66 20.09 16.67C20.06 16.74 19.67 18.11 18.71 19.5ZM13 3.5C13.73 2.67 14.94 2.04 15.94 2C16.07 3.17 15.6 4.35 14.9 5.19C14.21 6.04 13.07 6.7 11.95 6.61C11.8 5.46 12.36 4.26 13 3.5Z"/>
                  </svg>
                  <span>App Store</span>
                </Button>
                <Button 
                  onClick={handleGooglePlayClick}
                  className="btn-secondary flex items-center justify-center space-x-2"
                >
                  <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
                  </svg>
                  <span>Google Play</span>
                </Button>
              </div>
              <div className="flex items-center space-x-8 text-text-muted">
                <div className="flex items-center space-x-2">
                  <CheckCircle className="w-5 h-5 text-accent-primary" />
                  <span>Bez Maksas</span>
                </div>
                <div className="flex items-center space-x-2">
                  <CheckCircle className="w-5 h-5 text-accent-primary" />
                  <span>Ātra Reģistrācija</span>
                </div>
                <div className="flex items-center space-x-2">
                  <CheckCircle className="w-5 h-5 text-accent-primary" />
                  <span>Droši</span>
                </div>
              </div>
            </div>
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={heroInView ? { opacity: 1, scale: 1 } : {}}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative flex items-center justify-center"
            >
              <div className="relative rounded-2xl overflow-hidden shadow-2xl max-w-md mx-auto">
                <img 
                  src="https://customer-assets.emergentagent.com/job_community-markers/artifacts/duanu517_Telemap1.png"
                  alt="Telemap Mobile App"
                  className="w-full h-auto object-contain"
                  style={{ maxHeight: '600px' }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-bg-primary/60 to-transparent pointer-events-none"></div>
              </div>
              <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-accent-primary/20 rounded-full blur-3xl"></div>
              <div className="absolute -top-6 -left-6 w-32 h-32 bg-accent-primary/10 rounded-full blur-3xl"></div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" ref={featuresRef} className="py-20 px-6 bg-bg-secondary">
        <div className="container mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={featuresInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-4 text-white">Galvenās Funkcijas</h2>
            <p className="text-xl text-gray-300 max-w-2xl mx-auto">
              Telemap piedāvā visas nepieciešamās funkcijas, lai savienotu cilvēkus un sniegtu palīdzību
            </p>
          </motion.div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={featuresInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <Card className="feature-card p-8 h-full">
                  <div className="text-accent-primary mb-4">{feature.icon}</div>
                  <h3 className="text-xl font-semibold mb-3 text-white">{feature.title}</h3>
                  <p className="text-gray-300">{feature.description}</p>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" ref={howItWorksRef} className="py-20 px-6">
        <div className="container mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={howItWorksInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-4">Kā Tas Darbojas?</h2>
            <p className="text-xl text-text-secondary max-w-2xl mx-auto">
              Četri vienkārši soļi, lai sāktu palīdzēt vai saņemt palīdzību
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {steps.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={howItWorksInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: index * 0.15 }}
                className="relative"
              >
                <div className="text-center">
                  <div className="inline-flex items-center justify-center w-20 h-20 bg-accent-bg border-2 border-accent-primary rounded-full mb-6">
                    <span className="text-3xl font-bold text-accent-primary">{step.number}</span>
                  </div>
                  <h3 className="text-xl font-semibold mb-3">{step.title}</h3>
                  <p className="text-text-secondary">{step.description}</p>
                </div>
                {index < steps.length - 1 && (
                  <div className="hidden lg:block absolute top-10 left-[60%] w-[80%] h-0.5 bg-gradient-to-r from-accent-primary to-transparent"></div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Examples Section */}
      <section id="examples" ref={examplesRef} className="py-20 px-6 bg-bg-secondary">
        <div className="container mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={examplesInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-4">Lietošanas Piemēri</h2>
            <p className="text-xl text-text-secondary max-w-2xl mx-auto">
              Reālas situācijas, kurās Telemap var palīdzēt jums vai citiem
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {examples.map((example, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={examplesInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <Card className="feature-card overflow-hidden h-full">
                  <div className="relative overflow-hidden rounded-lg mb-4 bg-transparent p-4">
                    <img 
                      src={example.image} 
                      alt={example.title}
                      className="w-full h-auto object-contain mx-auto"
                      style={{ maxHeight: '400px' }}
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-semibold mb-3 text-white">{example.title}</h3>
                    <p className="text-gray-300">{example.description}</p>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Future Plans Section */}
      <section id="future" ref={futureRef} className="py-20 px-6">
        <div className="container mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={futureInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-4">Nākotnes Plāni</h2>
            <p className="text-xl text-text-secondary max-w-2xl mx-auto">
              Mēs nepārtraukti attīstāmies, lai piedāvātu jums vēl labāku pieredzi
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {futurePlans.map((plan, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={futureInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <Card className="feature-card p-8 text-center h-full">
                  <div className="inline-flex items-center justify-center w-20 h-20 bg-accent-bg rounded-2xl mb-6 text-accent-primary">
                    {plan.icon}
                  </div>
                  <h3 className="text-xl font-semibold mb-4">{plan.title}</h3>
                  <p className="text-text-secondary">{plan.description}</p>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 px-6 bg-bg-secondary">
        <div className="container mx-auto max-w-3xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">Biežāk Uzdotie Jautājumi</h2>
            <p className="text-xl text-text-secondary">
              Atbildes uz visbiežāk uzdotajiem jautājumiem par Telemap
            </p>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            <AccordionItem value="item-1" className="bg-bg-tertiary border border-border-subtle rounded-xl px-6">
              <AccordionTrigger className="text-lg font-semibold hover:text-accent-primary">
                Vai Telemap ir bezmaksas?
              </AccordionTrigger>
              <AccordionContent className="text-text-secondary">
                Jā! Telemap pašlaik ir pilnīgi bezmaksas lietojama aplikācija. Nākotnē plānojam ieviest premium funkcijas, bet pamata funkcionalitāte vienmēr būs pieejama bez maksas.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-2" className="bg-bg-tertiary border border-border-subtle rounded-xl px-6">
              <AccordionTrigger className="text-lg font-semibold hover:text-accent-primary">
                Vai Telemap ir drošs?
              </AccordionTrigger>
              <AccordionContent className="text-text-secondary">
                Jūsu drošība ir mūsu prioritāte. Mēs strādājam pie lietotāju verifikācijas sistēmas ieviešanas. Līdz tam iesakām satikties publiskās vietās un būt piesardzīgiem.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-3" className="bg-bg-tertiary border border-border-subtle rounded-xl px-6">
              <AccordionTrigger className="text-lg font-semibold hover:text-accent-primary">
                Kā es varu palīdzēt citiem?
              </AccordionTrigger>
              <AccordionContent className="text-text-secondary">
                Vienkārši atveriet aplikāciju, aplūkojiet karti un izvēlieties pieprasījumu, ko vēlaties izpildīt. Sazinieties ar personu un vienojieties par palīdzības sniegšanu.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-4" className="bg-bg-tertiary border border-border-subtle rounded-xl px-6">
              <AccordionTrigger className="text-lg font-semibold hover:text-accent-primary">
                Kad tiks ieviesta maksājumu sistēma?
              </AccordionTrigger>
              <AccordionContent className="text-text-secondary">
                Mēs aktīvi strādājam pie maksājumu sistēmas integrācijas. Plānojam to ieviest tuvāko mēnešu laikā, lai lietotāji varētu ērti norēķināties par sniegtajiem pakalpojumiem.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-5" className="bg-bg-tertiary border border-border-subtle rounded-xl px-6">
              <AccordionTrigger className="text-lg font-semibold hover:text-accent-primary">
                Vai Telemap darbojas tikai Latvijā?
              </AccordionTrigger>
              <AccordionContent className="text-text-secondary">
                Pašlaik Telemap ir fokusēts uz Latvijas tirgu, bet mēs plānojam paplašināties arī citās Baltijas valstīs tuvākajā nākotnē.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6">
        <div className="container mx-auto">
          <div className="bg-gradient-to-br from-bg-secondary to-bg-tertiary border border-border-subtle rounded-3xl p-12 md:p-16 text-center relative overflow-hidden">
            <div className="absolute inset-0 bg-accent-primary/5"></div>
            <div className="relative z-10">
              <h2 className="text-4xl md:text-5xl font-bold mb-6">
                Gatavs Sākt <span className="text-accent-primary">Palīdzēt?</span>
              </h2>
              <p className="text-xl text-text-secondary mb-8 max-w-2xl mx-auto">
                Pievienojies Telemap kopienai jau šodien un esi daļa no kustības, kas maina veidu, kā cilvēki palīdz viens otram Latvijā.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <Button 
                  onClick={handleAppStoreClick}
                  className="btn-primary flex items-center space-x-2"
                >
                  <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18.71 19.5C17.88 20.74 17 21.95 15.66 21.97C14.32 22 13.89 21.18 12.37 21.18C10.84 21.18 10.37 21.95 9.09997 22C7.78997 22.05 6.79997 20.68 5.95997 19.47C4.24997 17 2.93997 12.45 4.69997 9.39C5.56997 7.87 7.12997 6.91 8.81997 6.88C10.1 6.86 11.32 7.75 12.11 7.75C12.89 7.75 14.37 6.68 15.92 6.84C16.57 6.87 18.39 7.1 19.56 8.82C19.47 8.88 17.39 10.1 17.41 12.63C17.44 15.65 20.06 16.66 20.09 16.67C20.06 16.74 19.67 18.11 18.71 19.5ZM13 3.5C13.73 2.67 14.94 2.04 15.94 2C16.07 3.17 15.6 4.35 14.9 5.19C14.21 6.04 13.07 6.7 11.95 6.61C11.8 5.46 12.36 4.26 13 3.5Z"/>
                  </svg>
                  <span>App Store</span>
                </Button>
                <Button 
                  onClick={handleGooglePlayClick}
                  className="btn-secondary flex items-center space-x-2"
                >
                  <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
                  </svg>
                  <span>Google Play</span>
                </Button>
              </div>
              <div className="mt-8 flex items-center justify-center space-x-2 text-text-muted">
                <Users className="w-5 h-5" />
                <span>Pievienojies 1000+ lietotājiem Latvijā</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center space-x-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_community-markers/artifacts/kkamc9mv_logo.png" 
                alt="Telemap Logo" 
                className="w-8 h-8"
              />
              <span className="text-2xl font-bold">Telemap</span>
            </div>
            
            <div className="flex space-x-4">
              <a 
                href="https://www.tiktok.com/@telemap" 
                target="_blank" 
                rel="noopener noreferrer"
                className="footer-social-link"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/>
                </svg>
              </a>
              <a 
                href="https://www.facebook.com/telemap" 
                target="_blank" 
                rel="noopener noreferrer"
                className="footer-social-link"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
              </a>
              <a 
                href="https://www.instagram.com/telemap" 
                target="_blank" 
                rel="noopener noreferrer"
                className="footer-social-link"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                </svg>
              </a>
            </div>
            
            <p className="text-text-muted text-sm">&copy; 2025 Telemap. Visas tiesības aizsargātas.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
